import React, { useRef, useEffect } from 'react';
import { RotateCcw, Volume2 } from 'lucide-react';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import { useTranslation } from '../../../i18n';
import useEditorAutoGrow from './useEditorAutoGrow';

// Plays the clip from start; multiple clicks restart it. Mirrors the sound
// badge interaction so students re-listen by tapping again.
const AudioTile = ({ url, disabled, color, size = 'md', label }) => {
  const { t } = useTranslation();
  const audioRef = useRef(null);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handlePlay = (e) => {
    e.stopPropagation();
    if (!url) return;
    if (!audioRef.current) {
      audioRef.current = new Audio(resolveMediaUrl(url));
    }
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch(() => { /* gesture rejection — ignore */ });
  };

  const sizeClass = size === 'sm' ? 'w-6 h-6' : 'w-7 h-7';
  const iconSize = size === 'sm' ? 12 : 14;

  return (
    <button
      type="button"
      onClick={handlePlay}
      disabled={disabled || !url}
      aria-label={label || t('games.common.playAudio')}
      className={`${sizeClass} rounded-full flex items-center justify-center shadow-sm transition ${
        disabled || !url ? 'opacity-60 cursor-default' : 'hover:scale-110 active:scale-95'
      }`}
      style={{ backgroundColor: color }}
    >
      <Volume2 size={iconSize} className="text-white" />
    </button>
  );
};

const OddOneOutElement = ({
  element,
  isEditor = false,
  studentView = false,
  selections = {},
  showResult = false,
  perRowResults = null,
  onSelectionsChange,
  onCheck,
  onRetry,
  disabled = false,
  hideCheckButton = false,
  onAutoGrow,
}) => {
  const { t } = useTranslation();
  const { content, style } = element;
  const {
    rows = [],
    allowRetry = false,
    tileColor = '#3d6b3d',
    actionBarColor = '#f9fafb',
    fontFamily,
    fontSize: customFontSize,
    textColor = '#ffffff',
    alignment = 'center',
  } = content || {};

  const justifyClass = alignment === 'left'
    ? 'justify-start'
    : alignment === 'right'
      ? 'justify-end'
      : 'justify-center';

  const tileTextStyle = {
    color: textColor,
    ...(fontFamily ? { fontFamily } : {}),
    ...(customFontSize ? { fontSize: `${customFontSize}px` } : {}),
  };

  const backgroundColor = style?.backgroundColor || '#ffffff';
  const isActionBarTransparent = actionBarColor === 'transparent';

  const hasAnySelection = rows.some((r) => selections?.[r.id]);
  const allSelected = rows.length > 0 && rows.every((r) => selections?.[r.id]);

  const scrollRef = useRef(null);
  useEditorAutoGrow({
    enabled: isEditor,
    scrollRef,
    currentHeightPct: element.size?.height || 0,
    contentCount: rows.length,
    onAutoGrow,
  });

  const handlePick = (rowId, itemId) => {
    if (disabled || showResult || isEditor) return;
    onSelectionsChange?.({ ...selections, [rowId]: itemId });
  };

  const handleCheck = () => {
    if (!allSelected) return;
    onCheck?.(selections);
  };

  const handleRetry = () => {
    onRetry?.();
  };

  const correctCount = perRowResults
    ? Object.values(perRowResults).filter((v) => v?.isCorrect).length
    : 0;
  const overallCorrect = perRowResults && rows.length > 0 && correctCount === rows.length;

  // ---- EDITOR PREVIEW ----
  if (isEditor) {
    return (
      <div
        className="w-full h-full flex flex-col rounded-xl border-2 border-gray-200 overflow-hidden shadow-sm"
        style={{ backgroundColor }}
      >
        <div ref={scrollRef} className="flex-1 min-h-0 p-3 overflow-auto flex flex-col gap-2 justify-center">
          {rows.length === 0 ? (
            <p className="text-sm text-gray-400 text-center">{t('games.oddOneOut.addRowsInSettings')}</p>
          ) : (
            rows.map((row, rowIdx) => (
              <div key={row.id} className={`w-full flex items-center gap-2 flex-wrap ${justifyClass}`}>
                <span className="text-xs text-gray-400 font-medium w-5 text-right">{rowIdx + 1}.</span>
                {row.items.map((item, itemIdx) => {
                  const isOdd = item.id === row.oddId;
                  return (
                    <div
                      key={item.id}
                      className="px-2.5 py-1 rounded-lg text-xs font-medium flex items-center gap-1"
                      style={{ backgroundColor: tileColor, opacity: isOdd ? 1 : 0.85, ...tileTextStyle }}
                      title={isOdd ? t('games.oddOneOut.odd') : ''}
                    >
                      {item.kind === 'audio' ? (
                        <>
                          <Volume2 size={10} />
                          <span>{t('games.common.audio')}</span>
                        </>
                      ) : (
                        <span>{item.text || `—`}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // ---- INTERACTIVE / CLASSROOM ----
  return (
    <div
      className="w-full h-full flex flex-col rounded-xl overflow-hidden"
      style={{ backgroundColor: 'transparent' }}
    >
      {/* Rows */}
      <div className="flex-1 min-h-0 p-3 overflow-auto">
        <div className="flex flex-col gap-3">
          {rows.map((row, rowIdx) => {
            const picked = selections?.[row.id];
            const rowResult = perRowResults?.[row.id];
            const rowChecked = showResult && rowResult;
            return (
              <div
                key={row.id}
                className={`rounded-lg p-2 ${
                  rowChecked
                    ? rowResult.isCorrect
                      ? 'bg-green-50/60'
                      : 'bg-red-50/60'
                    : ''
                }`}
              >
                <div className={`flex items-center gap-2 flex-wrap ${justifyClass}`}>
                  <span className="text-xs font-semibold text-gray-500 w-5 text-right flex-shrink-0">
                    {rowIdx + 1}.
                  </span>
                  {row.items.map((item, itemIdx) => {
                    const isPicked = picked === item.id;
                    const correctIdForRow = rowResult?.correctId ?? row.oddId;
                    const isCorrectOdd = rowChecked && item.id === correctIdForRow;
                    const isWrongPick = rowChecked && isPicked && item.id !== correctIdForRow;

                    let ringClass = '';
                    if (isCorrectOdd) {
                      ringClass = 'ring-4 ring-green-300';
                    } else if (isWrongPick) {
                      ringClass = 'ring-4 ring-red-300';
                    } else if (isPicked && !showResult) {
                      ringClass = 'ring-4 ring-yellow-300';
                    }

                    if (item.kind === 'audio') {
                      return (
                        <div
                          key={item.id}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded-lg transition cursor-pointer ${
                            isPicked && !showResult ? 'scale-105' : ''
                          } ${ringClass}`}
                          style={{
                            backgroundColor: '#ffffff',
                            border: `2px solid ${isPicked ? tileColor : '#e5e7eb'}`,
                          }}
                          onClick={() => handlePick(row.id, item.id)}
                        >
                          <AudioTile
                            url={item.audioUrl}
                            disabled={disabled || showResult}
                            color={tileColor}
                            label={t('games.oddOneOut.rowAudioLabel', { row: rowIdx + 1, index: itemIdx + 1 })}
                          />
                          <span className="text-xs text-gray-500 font-medium select-none">
                            {itemIdx + 1}
                          </span>
                        </div>
                      );
                    }

                    return (
                      <button
                        type="button"
                        key={item.id}
                        onClick={() => handlePick(row.id, item.id)}
                        disabled={disabled || showResult}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition select-none ${
                          !disabled && !showResult ? 'hover:scale-105 active:scale-95' : ''
                        } ${ringClass}`}
                        style={{
                          backgroundColor: isPicked || !showResult ? tileColor : '#ffffff',
                          color: isPicked || !showResult ? textColor : '#1f2937',
                          border: `2px solid ${isPicked ? tileColor : '#e5e7eb'}`,
                          opacity: showResult && !isPicked && !isCorrectOdd ? 0.6 : 1,
                          ...(fontFamily ? { fontFamily } : {}),
                          ...(customFontSize ? { fontSize: `${customFontSize}px` } : {}),
                        }}
                      >
                        {item.text || '—'}
                      </button>
                    );
                  })}
                </div>
                {rowChecked && (rowResult?.explanation || row.explanation) && (
                  <p
                    className={`mt-1.5 ml-7 text-xs italic ${
                      rowResult.isCorrect ? 'text-green-700' : 'text-red-700'
                    }`}
                  >
                    {rowResult?.explanation || row.explanation}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Action bar */}
      <div
        className={`px-3 py-2 flex items-center justify-between gap-2 ${
          showResult || isActionBarTransparent ? '' : 'border-t border-gray-100'
        }`}
        style={{ backgroundColor: showResult ? 'transparent' : actionBarColor }}
      >
        {!showResult && (
          <span className="text-xs text-gray-400">
            {hasAnySelection
              ? allSelected
                ? t('games.common.readyToCheck')
                : t('games.oddOneOut.chooseOddProgress', { done: Object.keys(selections || {}).length, total: rows.length })
              : t('games.oddOneOut.chooseOddEachRow')}
          </span>
        )}

        <div className="flex items-center gap-2">
          {showResult && !overallCorrect && allowRetry && (
            <button
              onClick={handleRetry}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition"
            >
              <RotateCcw size={14} />
              {t('games.common.tryAgain')}
            </button>
          )}
          {!showResult && !hideCheckButton && (
            <button
              onClick={handleCheck}
              disabled={!allSelected}
              className={`px-4 py-1.5 text-sm font-medium rounded-lg transition ${
                allSelected
                  ? 'bg-indigo-500 text-white hover:bg-indigo-600'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {t('games.common.check')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Server/client validator — pure function so it can be reused from any handler.
export const validateOddOneOut = (rows = [], selections = {}) => {
  const perRowResults = {};
  let correctCount = 0;
  rows.forEach((row) => {
    const pick = selections?.[row.id];
    const isCorrect = pick === row.oddId;
    perRowResults[row.id] = { isCorrect, picked: pick || null, correctId: row.oddId };
    if (isCorrect) correctCount += 1;
  });
  return {
    perRowResults,
    correctCount,
    total: rows.length,
    isCorrect: rows.length > 0 && correctCount === rows.length,
  };
};

export default OddOneOutElement;
